import { ShieldCheck } from "@phosphor-icons/react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AGENCY, CARRIERS } from "@/lib/api";

export default function Footer() {
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollTo = (id) => {
    if (location.pathname === "/") document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    else navigate("/", { state: { scrollTo: id } });
  };
  return (
    <footer className="bg-midnight text-warm-white" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* brand + disclaimer */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="grid place-items-center w-9 h-9 rounded-md bg-navy text-gold">
                <ShieldCheck weight="fill" size={20} />
              </span>
              <span className="font-heading font-extrabold text-lg tracking-tight">{AGENCY.brand}</span>
            </div>
            <p className="mt-5 text-light-slate text-sm leading-relaxed max-w-sm">
              Independent Life &amp; Health insurance guidance. We help you compare and enroll
              through official, secure carrier portals.
            </p>

            <div className="mt-6 rounded-lg border border-gold/20 bg-gold/[0.05] p-4">
              <p className="text-[13px] leading-relaxed text-warm-white/90" data-testid="mandatory-disclaimer">
                <strong>Important:</strong> We are not affiliated with or endorsed by any government
                entity or Medicare. This website is for informational purposes and is not an offer of
                insurance. Coverage is subject to carrier approval and underwriting.
              </p>
            </div>
          </div>

          {/* quick links */}
          <div className="lg:col-span-3">
            <p className="eyebrow text-gold mb-4">Explore</p>
            <ul className="space-y-3 text-sm text-light-slate">
              {[["Life Insurance", "life"], ["Health Insurance", "health"], ["Solutions", "solutions"],
                ["Calculator", "calculator"], ["About Us", "about"], ["Book a Consultation", "booking"],
                ["Contact", "contact"]].map(([l, id]) => (
                <li key={id}>
                  <button onClick={() => scrollTo(id)} data-testid={`footer-link-${id}`}
                    className="hover:text-gold transition-colors">{l}</button>
                </li>
              ))}
              <li>
                <Link to="/blog" data-testid="footer-link-blog" className="hover:text-gold transition-colors">Blog</Link>
              </li>
            </ul>
          </div>

          {/* carrier portals */}
          <div className="lg:col-span-4">
            <p className="eyebrow text-gold mb-4">Official Application Portals</p>
            <div className="space-y-3">
              <PortalLink href={CARRIERS.health} testid="footer-carrier-health" name="HealthSherpa (ACA / Health)" />
              <PortalLink href={CARRIERS.life} testid="footer-carrier-life" name="Mutual of Omaha (Life)" />
              <PortalLink href={CARRIERS.ethos} testid="footer-carrier-ethos" name="Ethos (Term Life)" />
              <PortalLink href={CARRIERS.medicare} testid="footer-carrier-medicare" name="Medicare.gov (Resources)" />
            </div>
          </div>
        </div>

        {/* licensing block */}
        <div className="mt-14 pt-8 border-t border-white/10" data-testid="licensing-disclosure">
          <p className="eyebrow text-gold mb-3">Licensing & Disclosures</p>
          <p className="text-sm text-light-slate leading-relaxed max-w-4xl">
            {AGENCY.disclosure}
          </p>
        </div>

        {/* bottom bar */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-light-slate">© {year} {AGENCY.brand}. All rights reserved.</p>
          <div className="flex items-center gap-6 text-xs text-light-slate">
            <button onClick={() => scrollTo("contact")} data-testid="footer-privacy" className="hover:text-gold transition-colors">Privacy Policy</button>
            <button onClick={() => scrollTo("contact")} data-testid="footer-terms" className="hover:text-gold transition-colors">Terms of Service</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function PortalLink({ href, name, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="flex items-center justify-between rounded-lg border border-white/10 px-4 py-3 text-sm text-warm-white hover:border-gold hover:bg-white/[0.03] transition-all">
      {name}
      <span className="text-gold">↗</span>
    </a>
  );
}
